import React, { ReactNode, useState } from 'react';
import styled from 'styled-components';

import { SingleArrowDown, SingleArrowUp } from '@r1-frontend/ui-react/components/svg/arrows';
import { TSvgProps } from '@r1-frontend/ui-react/components/svg/createSvg';
import Svg from '@r1-frontend/ui-react/components/svg/Svg';
import ControlElement from '@r1-frontend/ui-react/components/typography/controlElement';
import { H4 } from '@r1-frontend/ui-react/components/typography/heading';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import TextWrapper from '@r1-frontend/ui-react/components/typography/TextWrapper';
import WebpImage from '@r1-frontend/ui-react/components/WebpImage';
import { BlockContainer, IndentContainer, RounderContainer } from '@r1-frontend/ui-react/experimental/containers';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

interface IImageProps {
    url: string,
    urlWebp?: string,
}

type TGiudeImage = {
    image: IImageProps,
    Icon?: never,
} | {
    image?: never,
    Icon: (props: TSvgProps) => JSX.Element,
}

export type TGuideItem = TGiudeImage & {
    isActive: boolean,
    title: string,

    description?: string | JSX.Element | ReactNode,
    info?: string | JSX.Element | ReactNode,
}
const checkActiveInfo = (isActive: TGuideItem['isActive'], info: TGuideItem['info']) => !!(isActive && info);

const GuideItem = ({
    image,
    Icon,
    title,
    description,
    info,
    isActive,
    ...props
}: TGuideItem) =>{
    const [open, setOpen] = useState(() => checkActiveInfo(isActive, info));

    const onToggle = () => {
        setOpen(!open);
    };

    return (
        <>
            <div {...props}>
                <RounderContainer $radius={8}>
                    {Icon && <Icon/>}
                    {image && (
                        <WebpImage urlWebp={image.urlWebp}>
                            <img src={image.url} alt='Иконка элемента списка'/>
                        </WebpImage>
                    )}
                </RounderContainer>
                <IndentContainer $mobile={[0]} $desktop={[0]}>
                    <TextWrapper>
                        <H4 $withIndent={false}>{title}</H4>
                        {description && (
                            typeof description === 'string'
                                ? <Paragraph4 data-test='description-guide' $color={COLORS.TextInfo} dangerouslySetInnerHTML={{ __html: description }}/>
                                : description
                        )}
                    </TextWrapper>
                    {info && (
                        <ControlElement Icon={open ? SingleArrowUp : SingleArrowDown} onClick={onToggle}>
                            {open ? 'Скрыть подробности' : 'Подробнее'}
                        </ControlElement>
                    )}
                </IndentContainer>
            </div>

            {open && (
                <IndentContainer $united={[0, 0, 16, 0]}>
                    <BlockContainer
                        indent={{ $desktop: [24], $mobile: [16] }}
                        rounder={{ $radius: 24 }}
                        bgColor={COLORS.BgLight}
                    >
                        <IndentContainer $united={[0]}>
                            {info}
                        </IndentContainer>
                    </BlockContainer>
                </IndentContainer>
            )}
        </>
    );
};

export const StyledGuideItem = styled(GuideItem).attrs(props => ({ className: props.isActive ? 'active' : '' }))`
    margin-bottom: 16px;
    flex-wrap: nowrap;
    position: relative;

    ${Paragraph4} {
        margin-bottom: 8px;
    }

    ${RounderContainer} {
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        padding: 8px;
        justify-content: center;
        align-items: center;
        border-radius: 12px;
        margin-right: 24px;
        background: ${props=> props.isActive ? COLORS.ButtonPrimary : COLORS.BgSurface};

        ${Svg} {
            #contour,
            #guts {
              stroke: ${props=> props.isActive ? COLORS.TextWhite : COLORS.TextPrimary};
            }
        }

        img,
        picture {
            width: 24px;
            height: 24px;
        }

        .active ~ & {
            ${Svg} {
                #contour,
                #guts {
                    stroke: ${COLORS.GRAY_DARK}
                }
            }
            background: ${COLORS.BgLight};
            &::after {
                background-color: ${COLORS.BgLight}
            }
        }

        &::after {
            content: "";
            position: absolute;
            bottom: -9px;
            height: calc(100% - 37px);
            width: 2px;
            background-color: ${props=> props.isActive ? COLORS.ButtonPrimary : COLORS.BgSurface};
        }
    }
  
    &:last-child {
        ${RounderContainer} {
            &::after{
                content: "";
                height: 0;
            }
        }
    }
`;
